/**
 * DEMO DATA, legacy taxonomy. This was authored against the earlier
 * Read/Write/Move/Share/Delete activity set. Keys were renamed mechanically
 * when the dashboard moved to the prototype's set: Read->exposed,
 * Write->modified, Share->shared carry their meaning; Move->created and
 * Delete->uploaded do NOT — those two are relabelled only so demo mode keeps
 * compiling. Live mode never reads this file.
 */
import {
  AiAgent,
  AiAgentContentItem,
  AiAgentEvent,
  AiAgentMatrixRow,
  AiFileActivity,
  AiFileActivityId,
  CategoryCount,
  GenAiEmployee,
  GenAiOverview,
  AI_RELATIONS,
  INFO_CATEGORIES,
  InfoCategoryDetail,
  InfoCategoryId,
  ActivityMatrixRow,
  LevelBreakdown,
  RiskCube,
  ShareTotals,
  by_category,
  by_relation,
  empty_levels,
} from "./model";

const category = (id: InfoCategoryId, count: number): CategoryCount => {
  const c = INFO_CATEGORIES.find((d) => d.id === id)!;
  return { id: c.id, name: c.name, icon: c.icon, count };
};

/**
 * Per-level splits from Ami's mockData.json. `count` stays the full total; the
 * cards recompute their headline from whichever levels the slider has active,
 * so the slider at its widest stop reproduces the number shown before it moved.
 */
const with_sensitivities = (
  id: InfoCategoryId,
  high: number,
  medium: number,
  low: number,
  none: number
): CategoryCount => ({
  ...category(id, high + medium + low + none),
  sensitivities: { high, medium, low, none },
});

/**
 * `w=AiContentRelation | x=Severity | y=InfoTypeLabel`, authored once.
 * Numbers are Ami's, transcribed from his prototype's CATEGORY_ACTIVITY_SEVERITY
 * and checked against the totals in his screenshots: 261 interactions overall,
 * 75 high severity, and relation rows of 160 / 45 / 18 / 25 / 13.
 *
 * One place his two mocks disagree: the cube puts Business at 63 while his
 * separate per-category mock says 62, so in his prototype the two cards
 * contradict each other on screen. The cube is the self-consistent number, so
 * that is the one that ships, and both cards derive from it.
 */
const lv = (high: number, medium: number, low: number, none: number) => ({
  high,
  medium,
  low,
  none,
});

const risk_cube: RiskCube = {
  personal: {
    exposed: lv(12, 18, 16, 8),
    modified: lv(5, 5, 3, 1),
    created: lv(2, 3, 1, 0),
    shared: lv(4, 2, 1, 1),
    uploaded: lv(2, 1, 1, 0),
  },
  hr: {
    exposed: lv(6, 9, 6, 3),
    modified: lv(3, 3, 2, 0),
    created: lv(1, 1, 1, 0),
    shared: lv(2, 1, 1, 0),
    uploaded: lv(0, 1, 0, 1),
  },
  legal: {
    exposed: lv(4, 7, 5, 2),
    modified: lv(2, 1, 2, 0),
    created: lv(1, 1, 0, 0),
    shared: lv(1, 1, 1, 0),
    uploaded: lv(0, 1, 0, 0),
  },
  governance: {
    exposed: lv(3, 4, 3, 1),
    modified: lv(1, 1, 1, 0),
    created: lv(0, 1, 0, 0),
    shared: lv(1, 0, 1, 0),
    uploaded: lv(0, 0, 0, 1),
  },
  business: {
    exposed: lv(10, 14, 10, 4),
    modified: lv(4, 4, 2, 1),
    created: lv(1, 2, 1, 0),
    shared: lv(2, 2, 1, 1),
    uploaded: lv(1, 2, 1, 0),
  },
  financial: {
    exposed: lv(4, 6, 4, 1),
    modified: lv(1, 1, 1, 1),
    created: lv(1, 0, 1, 0),
    shared: lv(1, 1, 0, 0),
    uploaded: lv(0, 1, 0, 0),
  },
};

/**
 * Demo pools for the share line, from gen-ai-ui's `riskShareMetrics`: 3,280
 * employee shares, 11,240 AI-originated activities and 38,400 events. The
 * previous period is the same pool with more risky activity, so the demo
 * reads as risk coming down.
 */
const demo_risky = (direction: "employee" | "ai") =>
  by_relation(risk_cube, "all", direction).reduce(
    (acc, r) => ({
      high: acc.high + r.severities.high,
      medium: acc.medium + r.severities.medium,
      low: acc.low + r.severities.low,
      none: 0,
    }),
    empty_levels()
  );

const scale_levels = (d: LevelBreakdown, k: number): LevelBreakdown => ({
  high: Math.round(d.high * k),
  medium: Math.round(d.medium * k),
  low: Math.round(d.low * k),
  none: 0,
});

const shares: ShareTotals = {
  current: {
    activity: { employee: 3280, ai: 11240 },
    ai_events: { employee: 3280, ai: 11240 },
    risky: { employee: demo_risky("employee"), ai: demo_risky("ai") },
    events: 38400,
  },
  previous: {
    activity: { employee: 3120, ai: 10630 },
    ai_events: { employee: 3120, ai: 10630 },
    risky: {
      employee: scale_levels(demo_risky("employee"), 1.5),
      ai: scale_levels(demo_risky("ai"), 1.45),
    },
    events: 39800,
  },
  period: "month",
};

/** derived, never authored — so it can never drift from the severity card */
/**
 * "# Shared By Employees" counts *people*, not interactions.
 *
 * It used to be `by_category(risk_cube)`, which made it a byte-for-byte copy of
 * the "Risk by information category" card — two cards side by side showing the
 * same six numbers, with nothing to tell a viewer they measured different
 * things. The totals below are the distinct employee counts from the
 * final-copilot-mock-data-v1 package (126 people across the six categories).
 *
 * That package carries no per-sensitivity split for employees, only totals, so
 * the slider's segments are apportioned from the category's own risk mix and
 * scaled to the employee total. Derived rather than typed out, so the segments
 * always re-sum to the total shown; swap in real splits here when the API
 * provides them.
 */
const EMPLOYEES_PER_CATEGORY: Record<InfoCategoryId, number> = {
  personal: 24,
  hr: 21,
  legal: 20,
  governance: 18,
  business: 23,
  financial: 20,
};

/** largest-remainder apportionment, so the parts sum to `total` exactly */
const apportion = (mix: number[], total: number): number[] => {
  const sum = mix.reduce((a, b) => a + b, 0);
  if (!sum) return mix.map(() => 0);
  const exact = mix.map((n) => (n / sum) * total);
  const out = exact.map((n) => Math.floor(n));
  let left = total - out.reduce((a, b) => a + b, 0);
  const order = exact
    .map((n, i) => ({ i, frac: n - Math.floor(n) }))
    .sort((a, b) => b.frac - a.frac);
  for (let k = 0; left > 0; k++, left--) {
    out[order[k % order.length].i]++;
  }
  return out;
};

const shared_by_employees: CategoryCount[] = by_category(risk_cube).map((c) => {
  const total = EMPLOYEES_PER_CATEGORY[c.id];
  const sv = c.severities || { high: 0, medium: 0, low: 0, none: 0 };
  const [high, medium, low, none] = apportion(
    [sv.high, sv.medium, sv.low, sv.none],
    total
  );
  return { ...c, count: total, severities: { high, medium, low, none } };
});

const ai_file_activity: AiFileActivity[] = [
  { id: "exposed", name: "Reads", icon: "Eye", count: 184 },
  { id: "modified", name: "Writes", icon: "Edit2", count: 46 },
  { id: "created", name: "Moves", icon: "DoubleArrows", count: 22 },
  { id: "shared", name: "Shares", icon: "ExposedTo", count: 31 },
  { id: "uploaded", name: "Deletes", icon: "Trash", count: 17 },
];

/**
 * "Information accessed by Copilot", from the final-copilot-mock-data package's
 * own table. These replace the figures transcribed from Ami's prototype
 * (3.1K / 2.4K / 1.6K / 1.1K / 842 / 316), which were an order of magnitude
 * above the events actually behind them — the card promised 3.1K and the
 * drill-down held 200. The fourth segment is the package's "Unclassified".
 */
const information_involved: CategoryCount[] = [
  with_sensitivities("personal", 33, 51, 77, 39),
  with_sensitivities("hr", 25, 40, 58, 32),
  with_sensitivities("legal", 20, 32, 32, 19),
  with_sensitivities("governance", 12, 21, 25, 13),
  with_sensitivities("business", 10, 16, 19, 9),
  with_sensitivities("financial", 4, 6, 6, 4),
];

const agents_by_activity: AiAgent[] = [
  { id: "copilot", name: "Microsoft 365 Copilot", icon: "Cogs", count: 1800 },
  { id: "hr_agent", name: "HR Agent", icon: "Group", count: 624 },
  { id: "research_agent", name: "Research Agent", icon: "Search", count: 418 },
  { id: "sales_agent", name: "Sales Agent", icon: "Statistics", count: 276 },
  { id: "others", name: "Others", icon: "MenuDots", count: 692 },
];

const agents_matrix: AiAgentMatrixRow[] = [
  {
    id: "copilot",
    name: "Microsoft 365 Copilot",
    icon: "Cogs",
    counts: {
      personal: 1200,
      hr: 842,
      legal: 612,
      governance: 428,
      business: 356,
      financial: 184,
    },
    highlight: "personal",
  },
  {
    id: "hr_agent",
    name: "HR Agent",
    icon: "Group",
    counts: {
      personal: 412,
      hr: 624,
      legal: 186,
      governance: 132,
      business: 96,
      financial: 48,
    },
    highlight: "hr",
  },
  {
    id: "research_agent",
    name: "Research Agent",
    icon: "Search",
    counts: {
      personal: 198,
      hr: 134,
      legal: 418,
      governance: 182,
      business: 68,
      financial: 36,
    },
    highlight: "legal",
  },
  {
    id: "sales_agent",
    name: "Sales Agent",
    icon: "Statistics",
    counts: {
      personal: 156,
      hr: 84,
      legal: 98,
      governance: 74,
      business: 276,
      financial: 52,
    },
    highlight: "business",
  },
  {
    id: "finance_agent",
    name: "Finance Agent",
    icon: "Finance",
    counts: {
      personal: 132,
      hr: 76,
      legal: 88,
      governance: 64,
      business: 118,
      financial: 212,
    },
    highlight: "financial",
  },
  {
    id: "legal_agent",
    name: "Legal Agent",
    icon: "Legal",
    counts: {
      personal: 94,
      hr: 62,
      legal: 168,
      governance: 108,
      business: 44,
      financial: 28,
    },
    highlight: "legal",
  },
  {
    id: "support_agent",
    name: "Support Agent",
    icon: "Support",
    counts: {
      personal: 134,
      hr: 48,
      legal: 22,
      governance: 18,
      business: 62,
      financial: 16,
    },
    highlight: "personal",
  },
  {
    id: "sharepoint_agent",
    name: "SharePoint Agent",
    icon: "Folder",
    counts: {
      personal: 72,
      hr: 40,
      legal: 34,
      governance: 96,
      business: 58,
      financial: 24,
    },
    highlight: "governance",
  },
];

const events: AiAgentEvent[] = [
  {
    id: "evt_1",
    by: "lea@cognni.ai",
    by_desc: "R&D Department",
    by_type: "employee",
    content: "policy-summary-for-copilot.docx",
    content_more: 2,
    content_label: "Personal Docs",
    piis: ["Name", "Email"],
    exposed_to: "M365 Copilot",
    exposed_to_desc: "AI Agent",
    exposed_to_type: "agent",
    date: "2026-06-10T17:42:00",
    relation: "exposed",
    severity: 1,
    handled: false,
    justification_requested: false,
    risk_type: "overSharing",
    info_type: "personal",
  },
  {
    id: "evt_2",
    by: "HR Agent",
    by_desc: "AI Agent",
    by_type: "agent",
    content: "employee_onboarding.docx",
    content_more: 0,
    content_label: "HR Files",
    piis: ["Name", "Address"],
    exposed_to: "Lea Cohen",
    exposed_to_desc: "HR Department",
    exposed_to_type: "employee",
    date: "2026-06-11T09:18:00",
    relation: "shared",
    severity: 2,
    handled: false,
    justification_requested: false,
    risk_type: "overExposure",
    info_type: "hr",
  },
  {
    id: "evt_3",
    by: "Research Agent",
    by_desc: "AI Agent",
    by_type: "agent",
    content: "salary_review_2026.xlsx",
    content_more: 3,
    content_label: "Compensation",
    piis: ["Name", "Salary", "ID"],
    exposed_to: "Amit Levi",
    exposed_to_desc: "R&D Department",
    exposed_to_type: "employee",
    date: "2026-06-11T09:11:00",
    relation: "exposed",
    severity: 3,
    handled: false,
    justification_requested: true,
    risk_type: "overExposure",
    info_type: "hr",
  },
  {
    id: "evt_4",
    by: "Lea Cohen",
    by_desc: "HR Department",
    by_type: "employee",
    content: "employee_reviews.docx",
    content_more: 1,
    content_label: "HR",
    piis: ["Name", "Employee ID"],
    exposed_to: "HR Agent",
    exposed_to_desc: "AI Agent",
    exposed_to_type: "agent",
    date: "2026-06-10T11:08:00",
    relation: "modified",
    severity: 3,
    handled: false,
    justification_requested: false,
    risk_type: "unsecureSharing",
    info_type: "hr",
  },
  {
    id: "evt_5",
    by: "Amit Levi",
    by_desc: "Legal Department",
    by_type: "employee",
    content: "merger-plan-draft.pdf",
    content_more: 0,
    content_label: "Legal",
    piis: ["Name"],
    exposed_to: "Research Agent",
    exposed_to_desc: "AI Agent",
    exposed_to_type: "agent",
    date: "2026-06-09T16:33:00",
    relation: "exposed",
    severity: 3,
    handled: false,
    justification_requested: true,
    risk_type: "overSharing",
    info_type: "legal",
  },
  {
    id: "evt_6",
    by: "Sales Agent",
    by_desc: "AI Agent",
    by_type: "agent",
    content: "lead_export_june.csv",
    content_more: 0,
    content_label: "CRM Export",
    piis: ["Name", "Email", "Phone"],
    exposed_to: "Daniel Rosen",
    exposed_to_desc: "Sales Department",
    exposed_to_type: "employee",
    date: "2026-06-09T13:18:00",
    relation: "shared",
    severity: 2,
    handled: false,
    justification_requested: false,
    risk_type: "lackOfLabeling",
    info_type: "business",
  },
  {
    id: "evt_7",
    by: "M365 Copilot",
    by_desc: "AI Agent",
    by_type: "agent",
    content: "board_minutes_q2.docx",
    content_more: 1,
    content_label: "Governance Records",
    piis: ["Name"],
    exposed_to: "Archive",
    exposed_to_desc: "SharePoint",
    exposed_to_type: "agent",
    date: "2026-06-12T10:05:00",
    relation: "created",
    severity: 2,
    handled: false,
    justification_requested: false,
    risk_type: "overSharing",
    info_type: "governance",
  },
  {
    id: "evt_8",
    by: "M365 Copilot",
    by_desc: "AI Agent",
    by_type: "agent",
    content: "salary_bands_2026.xlsx",
    content_more: 0,
    content_label: "Compensation",
    piis: ["Name", "Salary"],
    exposed_to: "Recycle bin",
    exposed_to_desc: "OneDrive",
    exposed_to_type: "agent",
    date: "2026-06-12T15:47:00",
    relation: "uploaded",
    severity: 3,
    handled: false,
    justification_requested: true,
    risk_type: "unusualActivity",
    info_type: "hr",
  },
  {
    id: "evt_9",
    by: "Finance Agent",
    by_desc: "AI Agent",
    by_type: "agent",
    content: "invoice_batch_0626.pdf",
    content_more: 3,
    content_label: "Invoices",
    piis: ["IBAN"],
    exposed_to: "Noa Peretz",
    exposed_to_desc: "Finance Department",
    exposed_to_type: "employee",
    date: "2026-06-13T08:30:00",
    relation: "exposed",
    severity: 1,
    handled: true,
    justification_requested: false,
    risk_type: "overExposure",
    info_type: "financial",
  },
  {
    id: "evt_10",
    by: "M365 Copilot",
    by_desc: "AI Agent",
    by_type: "agent",
    content: "nda_template_v4.docx",
    content_more: 0,
    content_label: "Legal Templates",
    piis: [],
    exposed_to: "Drafts",
    exposed_to_desc: "OneDrive",
    exposed_to_type: "agent",
    date: "2026-06-13T11:12:00",
    relation: "modified",
    severity: 2,
    handled: false,
    justification_requested: false,
    risk_type: "lackOfLabeling",
    info_type: "legal",
  },
  {
    id: "evt_11",
    by: "lea@cognni.ai",
    by_desc: "R&D Department",
    by_type: "employee",
    content: "customer_ids_dump.csv",
    content_more: 0,
    content_label: "Personal Data",
    piis: ["Name", "Email", "SSN"],
    exposed_to: "M365 Copilot",
    exposed_to_desc: "AI Agent",
    exposed_to_type: "agent",
    date: "2026-06-14T09:55:00",
    relation: "shared",
    severity: 3,
    handled: false,
    justification_requested: true,
    risk_type: "overSharing",
    info_type: "personal",
  },
  {
    id: "evt_12",
    by: "M365 Copilot",
    by_desc: "AI Agent",
    by_type: "agent",
    content: "old_contract_drafts.zip",
    content_more: 0,
    content_label: "Legal Archive",
    piis: ["Name"],
    exposed_to: "Recycle bin",
    exposed_to_desc: "SharePoint",
    exposed_to_type: "agent",
    date: "2026-06-14T16:20:00",
    relation: "uploaded",
    severity: 1,
    handled: false,
    justification_requested: false,
    risk_type: "unusualActivity",
    info_type: "legal",
  },
];

const content: AiAgentContentItem[] = [
  {
    id: "cnt_1",
    name: "policy-summary-for-copilot.docx",
    info_type: "Personal Docs",
    piis: "Name, Email",
    context: "Operations & Other",
    sensitivity: 2,
    application: "msft_sharepoint_365",
    accessed_by: "Microsoft 365 Copilot",
    info_category: "personal",
  },
  {
    id: "cnt_2",
    name: "employee_onboarding.docx",
    info_type: "HR Files",
    piis: "Name, Address",
    context: "Operations & Other",
    sensitivity: 2,
    application: "msft_onedrive",
    accessed_by: "HR Agent",
    info_category: "hr",
  },
  {
    id: "cnt_3",
    name: "salary_review_2026.xlsx",
    info_type: "Compensation",
    piis: "Name, Salary, ID",
    context: "Board & Management",
    sensitivity: 3,
    application: "msft_sharepoint_365",
    accessed_by: "Research Agent",
    info_category: "hr",
  },
  {
    id: "cnt_4",
    name: "employee_reviews.docx",
    info_type: "HR",
    piis: "Name",
    context: "Operations & Other",
    sensitivity: 3,
    application: "msft_teams",
    accessed_by: "HR Agent",
    info_category: "hr",
  },
  {
    id: "cnt_5",
    name: "merger-plan-draft.pdf",
    info_type: "Legal",
    piis: "",
    context: "Board & Management",
    sensitivity: 3,
    application: "msft_sharepoint_365",
    accessed_by: "Research Agent",
    info_category: "legal",
  },
  {
    id: "cnt_6",
    name: "lead_export_june.csv",
    info_type: "CRM Export",
    piis: "Name, Email, Phone",
    context: "Sales & Marketing",
    sensitivity: 2,
    application: "msft_onedrive",
    accessed_by: "Sales Agent",
    info_category: "business",
  },
];

/** sub-labels per category, mirroring the app's "What is your information?" cards */
const SUB_LABELS: Record<InfoCategoryId, string[]> = {
  personal: [
    "Personal Docs",
    "Contact Details",
    "Employee Records",
    "ID Documents",
    "Personal Addresses",
  ],
  hr: [
    "Accident Reports",
    "Compensation",
    "Employee Reviews",
    "HR Files",
    "Onboarding Material",
  ],
  legal: [
    "Acknowledgements",
    "Agreements",
    "Merger Documents",
    "NDAs",
    "Legal Opinions",
  ],
  governance: [
    "Audit Committee Agendas",
    "Audit Reports",
    "Board Meeting Agendas",
    "Board Meetings & Resolutions",
    "Policies",
  ],
  business: [
    "Bills of Sale",
    "Business Declarations",
    "Business Plans",
    "Certificates",
    "CRM Exports",
  ],
  financial: [
    "Accounting",
    "Banks",
    "Benefits & Insurance Forms",
    "Budgetary Reports & Planning",
    "Invoices",
  ],
};

/** splits a category total across its sub-labels, largest first */
const split_total = (id: InfoCategoryId, total: number) => {
  const names = SUB_LABELS[id];
  const weights = [0.34, 0.26, 0.18, 0.13, 0.09];
  return names.map((name, i) => ({
    id: `${id}_${i}`,
    name,
    count: Math.round(total * weights[i]),
  }));
};

const breakdown = (
  source: CategoryCount[],
  coverage: number[]
): InfoCategoryDetail[] =>
  source.map((c, i) => ({
    id: c.id,
    name: c.name,
    icon: c.icon,
    items: split_total(c.id, c.count),
    ms_coverage: coverage[i] ?? 0,
  }));

const info_breakdown = breakdown(
  information_involved,
  [12.4, 8.1, 4.2, 2.0, 0.6, 0.0]
);

const employee_breakdown = breakdown(
  shared_by_employees,
  [9.3, 6.7, 3.1, 1.4, 0.0, 0.0]
);

const EMPLOYEE_SEED: Array<[string, string, InfoCategoryId, number]> = [
  ["Lea Cohen", "HR Department", "hr", 34],
  ["Amit Levi", "Legal Department", "legal", 28],
  ["Daniel Rosen", "Sales Department", "business", 24],
  ["Maya Shalev", "Finance Department", "financial", 19],
  ["Noa Bar", "R&D Department", "personal", 17],
  ["Yotam Peled", "Operations", "governance", 12],
  ["Shira Adler", "HR Department", "hr", 9],
  ["Eitan Gil", "Legal Department", "legal", 7],
];

const employees: GenAiEmployee[] = EMPLOYEE_SEED.map(
  ([name, department, category, count], i) => ({
    id: `emp_${i}`,
    name,
    email: `${name.split(" ")[0].toLowerCase()}@cognni.ai`,
    department,
    category,
    count,
  })
);

/** the full agent roster behind the "AI agents by activity" expand */
const agents_all: AiAgent[] = [
  { id: "copilot", name: "Microsoft 365 Copilot", icon: "Cogs", count: 1800 },
  { id: "hr_agent", name: "HR Agent", icon: "Group", count: 624 },
  { id: "research_agent", name: "Research Agent", icon: "Search", count: 418 },
  { id: "sales_agent", name: "Sales Agent", icon: "Statistics", count: 276 },
  { id: "finance_agent", name: "Finance Agent", icon: "Finance", count: 212 },
  { id: "legal_agent", name: "Legal Agent", icon: "Legal", count: 168 },
  { id: "support_agent", name: "Support Agent", icon: "Support", count: 134 },
  { id: "sharepoint_agent", name: "SharePoint Agent", icon: "Folder", count: 96 },
  { id: "security_copilot", name: "Security Copilot", icon: "Shield", count: 82 },
];

// three levels only, matching the approved build — the percentages are meant
// to total 100 (47/31/22); adding a fourth row skews them
/**
 * "Activity vs. Information Category" — the matrix rows are the four AI file
 * operations, reusing the same per-category columns.
 */
const activity_matrix: ActivityMatrixRow[] = [
  {
    id: "exposed",
    name: "Reads",
    icon: "Eye",
    counts: {
      personal: 1200,
      hr: 842,
      legal: 612,
      governance: 428,
      business: 356,
      financial: 184,
    },
  },
  {
    id: "modified",
    name: "Writes",
    icon: "Edit2",
    counts: {
      personal: 412,
      hr: 624,
      legal: 186,
      governance: 132,
      business: 96,
      financial: 48,
    },
  },
  {
    id: "created",
    name: "Moves",
    icon: "DoubleArrows",
    counts: {
      personal: 198,
      hr: 134,
      legal: 418,
      governance: 182,
      business: 68,
      financial: 36,
    },
  },
  {
    id: "shared",
    name: "Shares",
    icon: "ExposedTo",
    counts: {
      personal: 284,
      hr: 196,
      legal: 142,
      governance: 98,
      business: 112,
      financial: 64,
    },
  },
  {
    id: "uploaded",
    name: "Deletes",
    icon: "Trash",
    counts: {
      personal: 156,
      hr: 84,
      legal: 98,
      governance: 74,
      business: 276,
      financial: 52,
    },
  },
];

/**
 * Drill-down coverage for the activity matrix.
 *
 * The twelve curated events above are written for realism, not coverage, and
 * between them they only occupy 12 of the 30 activity x category cells. The
 * other 18 cells show a number on the card and then opened an empty events
 * list when clicked — the worst version of the bug, because the number implies
 * there is something to see.
 *
 * These fill the gaps from the same vocabulary the curated events use: the
 * category's own sub-labels for content, real seeded employees for the actor,
 * and all three severities so the modal still has something to show at every
 * stop of the severity slider. Curated events stay first, so they are what a
 * viewer meets at the top of any list they open.
 */
const RELATION_VERB: Record<AiFileActivityId, [string, string]> = {
  exposed: ["summary", "overSharing"],
  modified: ["draft", "lackOfLabeling"],
  created: ["archive", "unusualActivity"],
  shared: ["shared-copy", "unsecureSharing"],
  uploaded: ["cleanup", "overExposure"],
};

const EXT_BY_CATEGORY: Record<InfoCategoryId, string> = {
  personal: "docx",
  hr: "xlsx",
  legal: "pdf",
  governance: "pdf",
  business: "pptx",
  financial: "xlsx",
};

// keyed activity|category|severity, because the severity slider filters by
// exact level rather than a threshold: a cell holding only medium rows still
// opens empty at the "High only" stop
const covered = new Set(
  events.map((e) => e.relation + "|" + e.info_type + "|" + e.severity)
);

const coverage_events: AiAgentEvent[] = [];
INFO_CATEGORIES.forEach((cat, ci) => {
  AI_RELATIONS.forEach((rel, ri) => {
    const labels = SUB_LABELS[cat.id];
    const [verb, risk] = RELATION_VERB[rel.id];
    // one row per severity, so narrowing the slider never empties a list that
    // still shows a count; curated rows are left in place and only the
    // severities they do not already cover are filled in
    [1, 2, 3].forEach((severity, si) => {
      if (covered.has(rel.id + "|" + cat.id + "|" + severity)) {
        return;
      }
      const seed = EMPLOYEE_SEED[(ci * 5 + ri * 3 + si) % EMPLOYEE_SEED.length];
      const label = labels[(ri + si) % labels.length];
      coverage_events.push({
        id: `evt_cov_${cat.id}_${rel.id}_${severity}`,
        by: seed[0],
        by_desc: seed[1],
        by_type: "employee",
        content: `${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-${verb}.${
          EXT_BY_CATEGORY[cat.id]
        }`,
        content_more: si,
        content_label: label,
        piis: cat.id === "personal" || cat.id === "hr" ? ["Name", "Email"] : [],
        exposed_to: "M365 Copilot",
        exposed_to_desc: "AI Agent",
        exposed_to_type: "agent",
        // spread across the month so the list is not one timestamp repeated
        date: `2026-06-${String(((ci * 5 + ri * 3 + si) % 27) + 1).padStart(
          2,
          "0"
        )}T09:${String((si * 17 + ri * 7) % 60).padStart(2, "0")}:00`,
        relation: rel.id,
        severity,
        handled: false,
        justification_requested: false,
        risk_type: risk,
        info_type: cat.id,
      } as AiAgentEvent);
    });
  });
});

const events_all: AiAgentEvent[] = [...events, ...coverage_events];

export const GEN_AI_MOCK: GenAiOverview = {
  content,
  risk_cube,
  // demo approximations — the legacy mock never had a separate sensitivity
  // cube or distinct-employee counts
  sensitivity_cube: risk_cube,
  employees_by_category: shared_by_employees.map((c) => ({
    ...c,
    sensitivities: c.severities,
  })),
  // per level, the widest category stands in for distinct people overall
  employees_by_level: shared_by_employees.reduce((acc, c) => {
    (Object.keys(acc) as (keyof LevelBreakdown)[]).forEach((k) => {
      acc[k] = Math.max(acc[k], (c.severities && c.severities[k]) || 0);
    });
    return acc;
  }, empty_levels()),
  shares,
  activity_matrix,
  info_breakdown,
  employee_breakdown,
  employees,
  agents_all,
  shared_by_employees,
  ai_file_activity,
  information_involved,
  agents_by_activity,
  agents_matrix,
  events: events_all,
};
